import { Button, IconButton } from 'components/core';
import Image from "next/legacy/image";
import { FC } from 'react';
import { ClamshellTemplateProps, Styled } from './';
import { useComponentVisible } from 'utils/hooks';
import { useDeleteTemplate } from 'reactQuery/mutations';
import { LoadingSpinner } from 'styles/sharedStyles';
import { ClamShellTemplate } from 'types';

const ClamshellTemplate: FC<ClamshellTemplateProps> = ({
  clamshellTemplate,
  setSelectedTemplate,
  setTemplateMode,
}) => {
  const {
    ref: actionsRef,
    isComponentVisible: showAcctionsMenu,
    setIsComponentVisible: setShowAcctionsMenu,
  } = useComponentVisible(false);

  const deleteTemplateMutation = useDeleteTemplate();

  const deleteTemplate = (templateCode: string) => {
    deleteTemplateMutation.mutate({ templateCode });
    setShowAcctionsMenu(false);
  };

  const updateTemplate = (template: ClamShellTemplate) => {
    setShowAcctionsMenu(false);
    setSelectedTemplate(template);
    setTemplateMode('edit');
  };

  const cloneTemplate = (template: ClamShellTemplate) => {
    setShowAcctionsMenu(false);
    setSelectedTemplate(template);
    setTemplateMode('clone');
  };

  const selectTempleForOrder = (template: ClamShellTemplate) => {
    setSelectedTemplate(template);
    setTemplateMode('select');
  };

  const isTemplateRejected = clamshellTemplate.status.includes('reject');

  return (
    <>
      <Styled.TemplateContainer>
        {deleteTemplateMutation.isLoading && (
          <Styled.LoaderContainer>
            <LoadingSpinner isPrimary={true} width="20px" height="20px" />
          </Styled.LoaderContainer>
        )}
        <Styled.TemplateBody isLoading={deleteTemplateMutation.isLoading}>
          <Styled.ActionButtonsWrapper>
            <div ref={actionsRef}>
              <IconButton
                onClick={() => setShowAcctionsMenu(!showAcctionsMenu)}
                size="sm"
              >
                <Image
                  src="/images/icons/settings.svg"
                  alt="me"
                  width="18"
                  height="18"
                />
              </IconButton>
              {showAcctionsMenu && (
                <Styled.Menu>
                  {(clamshellTemplate.status === 'pending' ||
                    clamshellTemplate.status === 'align_rejected' ||
                    clamshellTemplate.status === 'printing_rejected') && (
                    <Styled.MenuItem
                      onClick={() => updateTemplate(clamshellTemplate)}
                    >
                      <Image
                        src="/images/icons/edit.svg"
                        alt="me"
                        width="18"
                        height="18"
                      />
                      Edit Template
                    </Styled.MenuItem>
                  )}

                  <Styled.MenuItem
                    onClick={() => cloneTemplate(clamshellTemplate)}
                  >
                    <Image
                      src="/images/icons/copy.svg"
                      alt="me"
                      width="18"
                      height="18"
                    />
                    Clone Template
                  </Styled.MenuItem>
                  {!clamshellTemplate.has_orders && (
                    <Styled.MenuItem
                      onClick={() => deleteTemplate(clamshellTemplate.code)}
                    >
                      <Image
                        src="/images/icons/delete.svg"
                        alt="me"
                        width="18"
                        height="18"
                      />
                      Delete Template
                    </Styled.MenuItem>
                  )}
                </Styled.Menu>
              )}
            </div>
          </Styled.ActionButtonsWrapper>
          <Styled.ImageWrapper>
            <Styled.ProductImage>
              <Image
                src={clamshellTemplate.previewUrl}
                alt="Prod Image"
                layout="fill"
                objectFit="contain"
                sizes="25vw"
                placeholder="blur"
                blurDataURL="data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg=="
              />
            </Styled.ProductImage>
          </Styled.ImageWrapper>
          <Styled.ProductDetails>
            <Styled.ProductTitle>{clamshellTemplate.name}</Styled.ProductTitle>

            <Styled.QuickAddToCart className="quickAddToCart">
              <Styled.ButtonWrapper>
                <Button
                  variant="primary"
                  size="md"
                  fullWidth={true}
                  // disabled={!product.inStock || addLineItemMutation.isLoading}
                  // loading={addLineItemMutation.isLoading}
                  className="quick-add-to-cart-gtm"
                  onClick={() => selectTempleForOrder(clamshellTemplate)}
                  disabled={isTemplateRejected}
                >
                  Select this template
                </Button>
              </Styled.ButtonWrapper>
            </Styled.QuickAddToCart>
          </Styled.ProductDetails>
        </Styled.TemplateBody>
      </Styled.TemplateContainer>
    </>
  );
};

export default ClamshellTemplate;
